import React from 'react';
import { View } from 'react-native';
import Icon from '../../../v2BaseComponents/Icon';
import PressableDynamicOpacity from '../../../v2BaseComponents/PressableDynamicOpacity';
import Stack from '../../../v2BaseComponents/Stack';
import Text from '../../../v2BaseComponents/Text';
import { colors } from '../../../../utils/theme';
import { getIntl } from '../../../../utils/intl';
import type { NoteLinkState } from '../utils/getNoteLinkState';

const intl = getIntl('note-link-pill', {
    linked: 'Linked',
    retained: 'Retained',
});

export type NoteLinkPillProps = {
    /** Linked: the note tracks, or is tracked by, a counterpart note. Retained: it once was and now stands alone. */
    state: NoteLinkState;
    /** Opens the counterpart note; omitted when the reader cannot view it. */
    onPress?: () => void;
    testID?: string;
};

/**
 * The pill beside a note's title that shows its link to a counterpart note: a client-record copy's link to the
 * profile note it tracks, or a profile note's link to the copy the reader's organization holds.
 */
const NoteLinkPill: React.FC<NoteLinkPillProps> = ({ state, onPress, testID }) => {
    const content = (
        <Stack
            horizontal
            align="center"
            space={1}
            style={{
                backgroundColor: state === 'linked' ? colors['Primary-Reg'] : colors['Primary-Light'],
                borderRadius: 12,
                paddingHorizontal: 8,
                paddingVertical: 3,
            }}
        >
            <Icon name={state === 'linked' ? 'Link' : 'LinkBroken'} size="checkbox-icon" color="Dark-Text" />
            <Text variant="caption-1" color="Dark-Text">
                {intl(state)}
            </Text>
        </Stack>
    );

    return onPress ? (
        <PressableDynamicOpacity onPress={onPress} testID={testID}>
            {content}
        </PressableDynamicOpacity>
    ) : (
        <View testID={testID}>{content}</View>
    );
};

export default React.memo(NoteLinkPill);
